import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsService } from '../services/products.service';
import { Plus, Settings, BarChart3, Users, Pencil, Check, X, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export function ProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { data, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.list(),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  interface DeleteTarget {
    id: number;
    name: string;
    counts: {
      leads: number;
      clients: number;
      users: number;
      emailCampaigns: number;
      integrations: number;
      financialTransactions: number;
    };
  }
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: any) => productsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setShowCreate(false);
      setForm({ name: '', slug: '', description: '' });
      toast.success('Produto criado com sucesso');
    },
    onError: () => toast.error('Erro ao criar produto'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => productsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setEditingId(null);
      toast.success('Produto atualizado');
    },
    onError: () => toast.error('Erro ao atualizar produto'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => productsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setDeleteTarget(null);
      setDeleteConfirm('');
      setAcknowledged(false);
      toast.success('Produto removido');
    },
    onError: () => toast.error('Erro ao remover produto'),
  });

  const products: any[] = data?.data || data || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Produtos</h1>
          <p className="text-ink-secondary">Gerenciamento de produtos e pipelines</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-ink-primary text-canvas px-4 py-2 rounded-lg hover:bg-ink-secondary text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo Produto
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink-primary" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-surface rounded-[14px] border border-border">
          <p className="text-ink-tertiary">Nenhum produto cadastrado ainda.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 text-sm text-ink-primary hover:text-ink-primary font-medium"
          >
            Criar primeiro produto
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product: any) => (
            <div key={product.id} className="bg-surface rounded-xl border border-border p-5">
              <div className="flex items-start justify-between mb-3">
                {editingId === product.id ? (
                  <div className="flex-1 space-y-2 mr-2">
                    <input
                      className="input w-full text-sm"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="Nome do produto"
                      autoFocus
                    />
                    <textarea
                      className="input w-full text-sm"
                      rows={2}
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Descrição"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => updateMutation.mutate({ id: product.id, data: editForm })}
                        disabled={!editForm.name || updateMutation.isPending}
                        className="p-1.5 rounded-lg bg-subtle text-accent hover:bg-subtle disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1.5 rounded-lg bg-canvas text-ink-tertiary hover:bg-subtle"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div>
                      <h3 className="font-semibold text-ink-primary">{product.name}</h3>
                      <p className="text-sm text-ink-tertiary">{product.slug}</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingId(product.id);
                        setEditForm({ name: product.name, description: product.description || '' });
                      }}
                      className="p-1 rounded hover:bg-subtle text-ink-tertiary hover:text-ink-primary mt-0.5"
                      title="Editar nome"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    product.active ? 'bg-subtle text-accent' : 'bg-subtle text-ink-tertiary'
                  }`}
                >
                  {product.active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              {editingId !== product.id && product.description && (
                <p className="text-sm text-ink-secondary mb-3">{product.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-ink-tertiary mb-4">
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3.5 h-3.5" /> {product._count?.leads ?? 0} leads
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> {product._count?.users ?? 0} usuários
                </span>
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => navigate(`/config/produto/${product.id}`)}
                  className="flex items-center gap-1.5 text-sm text-ink-primary hover:text-ink-primary font-medium transition-colors"
                >
                  <Settings className="w-4 h-4" /> Configurar
                </button>
                {isAdmin && (
                  <button
                    onClick={() =>
                      setDeleteTarget({
                        id: product.id,
                        name: product.name,
                        counts: {
                          leads: product._count?.leads ?? 0,
                          clients: product._count?.clients ?? 0,
                          users: product._count?.users ?? 0,
                          emailCampaigns: product._count?.emailCampaigns ?? 0,
                          integrations: product._count?.integrations ?? 0,
                          financialTransactions: product._count?.financialTransactions ?? 0,
                        },
                      })
                    }
                    className="p-1.5 rounded text-ink-tertiary hover:text-error hover:bg-subtle transition-colors"
                    title="Remover produto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Novo Produto</h3>
            <div className="space-y-4">
              <div>
                <label className="label">Nome</label>
                <input
                  className="input w-full"
                  value={form.name}
                  placeholder="Ex: CRM Vendas"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                      slug: e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, ''),
                    })
                  }
                />
              </div>
              <div>
                <label className="label">Slug</label>
                <input
                  className="input w-full"
                  value={form.slug}
                  placeholder="crm-vendas"
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                />
                <p className="text-xs text-ink-tertiary mt-1">
                  Identificador único (somente letras minúsculas, números e hífens)
                </p>
              </div>
              <div>
                <label className="label">Descrição</label>
                <textarea
                  className="input w-full"
                  rows={3}
                  value={form.description}
                  placeholder="Descrição opcional do produto"
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setForm({ name: '', slug: '', description: '' });
                }}
                className="px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary"
              >
                Cancelar
              </button>
              <button
                onClick={() => createMutation.mutate(form)}
                disabled={!form.name || !form.slug || createMutation.isPending}
                className="px-4 py-2 text-sm bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending ? 'Criando...' : 'Criar Produto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal — destructive, requires typing name + acknowledgement */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-md mx-4 shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-error">Remover produto</h3>

            <p className="text-sm text-ink-secondary">
              Esta ação remove o produto <strong>{deleteTarget.name}</strong> e{' '}
              <strong>todos os dados vinculados</strong>. Audit logs são preservados.
            </p>

            {(() => {
              const items = [
                { label: 'leads', count: deleteTarget.counts.leads },
                { label: 'clientes', count: deleteTarget.counts.clients },
                { label: 'campanhas', count: deleteTarget.counts.emailCampaigns },
                { label: 'integrações', count: deleteTarget.counts.integrations },
                {
                  label: 'transações financeiras',
                  count: deleteTarget.counts.financialTransactions,
                },
                { label: 'vínculos de usuários', count: deleteTarget.counts.users },
              ].filter((it) => it.count > 0);

              if (items.length === 0) {
                return (
                  <p className="text-sm text-ink-tertiary italic">
                    Este produto não tem dados vinculados.
                  </p>
                );
              }

              return (
                <div className="bg-canvas rounded-lg border border-error/30 p-3 space-y-1 text-sm">
                  <p className="font-medium text-error">Vai ser apagado:</p>
                  <ul className="text-ink-secondary space-y-0.5 ml-2">
                    {items.map((it) => (
                      <li key={it.label}>
                        • <strong className="text-ink-primary">{it.count}</strong> {it.label}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            <label className="flex items-start gap-2 text-sm text-ink-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Entendo que esta operação é <strong>irreversível</strong> e que vou perder todos os
                dados acima.
              </span>
            </label>

            <div>
              <p className="text-sm text-ink-secondary mb-1">
                Para confirmar, digite{' '}
                <code className="bg-canvas px-1 rounded">{deleteTarget.name}</code>:
              </p>
              <input
                autoFocus
                className="input w-full"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={deleteTarget.name}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirm('');
                  setAcknowledged(false);
                }}
                className="px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary"
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={
                  deleteConfirm !== deleteTarget.name || !acknowledged || deleteMutation.isPending
                }
                className="px-4 py-2 text-sm bg-error text-canvas rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                {deleteMutation.isPending ? 'Removendo...' : 'Remover definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsPage;
